import React from 'react';
import { View, useWindowDimensions } from 'react-native';
import { InputParlevelQuantity } from '../../components/input';
import { MEASUREMENT_UNIT_SHORT_NAME } from '../../../common/constants';
import { formatParlevelAndQuantity } from '../../../common/utils/utils';
import { useConfirmStockLevelsContext } from '../../utils/context';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { Counter, SecondaryButton } from '../../design/components';

// Three full-size counters need about 350 pt (more with 3-digit values); narrower
// phones (e.g. 360 pt wide) use compact counters so Present, Spoiled and Removed stay on one row.
const COMPACT_COUNTERS_BELOW_WIDTH = 400;

const ConfirmStockLevelsButtons = ({ planogramItem, showAdvancedView }) => {
	const styles = useThemedStyles(createStyles);
	const { width: windowWidth } = useWindowDimensions();
	const isCompact = showAdvancedView && windowWidth < COMPACT_COUNTERS_BELOW_WIDTH;
	const {
		changeStockItem,
		onReportIncorrectStockSetting,
		hasReportedStockSetting,
	} = useConfirmStockLevelsContext();
	const showReportAction = Boolean(planogramItem?.product && onReportIncorrectStockSetting);
	const reportAction = showReportAction ? (
		<SecondaryButton
			variant="tonal"
			label={hasReportedStockSetting?.(planogramItem.id) ? 'Reported' : 'Report a wrong value'}
			onPress={() => onReportIncorrectStockSetting(planogramItem.id)}
		/>
	) : null;
	const formatValue = value => formatParlevelAndQuantity(value, planogramItem.product);
	const renderCounter = (field, label) => (
		<Counter
			label={label}
			value={planogramItem[field] || 0}
			formatValue={formatValue}
			compact={isCompact}
			onChange={changeStockItem(planogramItem.id, field)}
		/>
	);

	if (planogramItem.isIngredient) {
		return (
			<View style={styles.column}>
				<View style={styles.ingredientInput}>
					<InputParlevelQuantity
						product={planogramItem.product}
						onChange={changeStockItem(planogramItem.id, 'presentProductQuantity')}
						value={planogramItem.presentProductQuantity}
						suffix={planogramItem.product && MEASUREMENT_UNIT_SHORT_NAME[planogramItem.product.measurementUnit]}
					/>
				</View>
				{reportAction}
			</View>
		);
	}

	if (!showAdvancedView) {
		return (
			<View style={styles.column}>
				{renderCounter('presentProductQuantity')}
				{reportAction}
			</View>
		);
	}

	return (
		<View style={styles.column}>
			<View style={styles.counters}>
				{renderCounter('presentProductQuantity', 'Present')}
				{renderCounter('spoiledProductQuantity', 'Spoiled')}
				{renderCounter('removedProductQuantity', 'Removed')}
			</View>
			{reportAction}
		</View>
	);
};

const createStyles = ({ ui: { space } }) => ({
	column: {
		gap: space.md,
	},
	counters: {
		flexDirection: 'row',
		// Compact counters fit one row from 360 wide; wrapping only catches tinier screens.
		flexWrap: 'wrap',
		justifyContent: 'space-between',
		rowGap: space.sm,
	},
	ingredientInput: {
		flexDirection: 'row',
		alignItems: 'center',
	},
});

export default React.memo(ConfirmStockLevelsButtons);
